import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSport } from "@/context/SportContext";
import api from "@/lib/api";
import RichText from "@/lib/richText";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SocialsFields, { socialsToRows, rowsToSocials } from "@/components/SocialsFields";
import MultiSelect from "@/components/MultiSelect";
import CircuitSelect from "@/components/CircuitSelect";
import PhoneInput from "@/components/PhoneInput";
import PhotoUpload from "@/components/PhotoUpload";
import PlayerProfileForm from "@/components/PlayerProfileForm";
import ClaimedListingsPanel from "@/components/ClaimedListingsPanel";
import AlertsPanel from "@/components/AlertsPanel";
import NameFields, { splitName } from "@/components/NameFields";
import ContactsField from "@/components/ContactsField";
import { sortRosters, DEFAULT_SORT_STATE, hasCoach, teamIdentifier } from "@/lib/teamSort";
import RosterSortControls from "@/components/RosterSortControls";
import { gradYearOptions } from "@/lib/gradYears";
import { toast } from "sonner";
import { User, Mail, X, Plus, Edit3, Trash2, MessageSquare, Send, Bell, Sun, Moon, Shield, Repeat2, Check, ChevronDown, ChevronUp } from "lucide-react";
import AdministratorsManager from "@/components/CollaboratorsManager";
import { useTheme } from "@/context/ThemeContext";
import PostComposer from "@/components/PostComposer";
import AvatarPicker from "@/components/AvatarPicker";
import PostMedia from "@/components/PostMedia";
import PollView from "@/components/PollView";
import PostActions from "@/components/PostActions";
import ScheduledManager from "@/components/ScheduledManager";

function AppearancePanel() {
    const { theme, setTheme } = useTheme();
    const opts = [
        { v: "dark", label: "Dark", icon: Moon },
        { v: "light", label: "Light", icon: Sun },
    ];
    return (
        <div className="mt-8" data-testid="appearance-panel">
            <div className="flex items-center gap-2 mb-3">
                <Sun className="h-4 w-4 text-[#ff4500]" />
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Appearance</div>
            </div>
            <div className="border border-[#2a2a2a] bg-[#141414] p-4">
                <div className="text-[11px] text-[#a0a0a0] mb-3">Choose how the site looks. Your choice is saved on this device.</div>
                <div className="flex gap-3">
                    {opts.map(({ v, label, icon: Icon }) => (
                        <button
                            key={v}
                            type="button"
                            data-testid={`theme-option-${v}`}
                            onClick={() => setTheme(v)}
                            className={`flex-1 h-12 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-semibold border transition-colors ${theme === v ? "bg-[#ff4500] border-[#ff4500] text-white" : "bg-[#0f0f0f] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500]"}`}
                        >
                            <Icon className="h-4 w-4" /> {label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
import { PROFILE_LABEL, BETA_NOTICE, isProUser, TITLES, titleColor, ACCOUNT_TYPES, ACCOUNT_TYPE_LABEL, ACCOUNT_TYPE_TAGLINE, ACCOUNT_TYPE_FEATURES, ACCOUNT_TYPE_PRICE } from "@/lib/profiles";
import { agesForSport, circuitsForSport, GRADES } from "@/lib/sports";
import { ALL_EVENT_TYPES, EVENT_LABELS, EVENT_HELP, DEFAULT_CHANNELS, DEFAULT_DEFAULTS } from "@/lib/notifications";
import TipLinksField, { linksToRows, rowsToLinks } from "@/components/TipLinksField";

function IdentityPanel() {
    const { user, updateIdentity } = useAuth();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const legacy = splitName(user.name);
    const pro = isProUser(user);
    const [form, setForm] = useState({
        first_name: user.first_name || legacy.first_name || "",
        middle_initial: user.middle_initial || legacy.middle_initial || "",
        last_name: user.last_name || legacy.last_name || "",
        username: user.username || "",
        pro_title: user.pro_title || "",
    });
    const [contacts, setContacts] = useState(user.contacts && user.contacts.length ? user.contacts : []);

    const open = () => {
        const lg = splitName(user.name);
        setForm({
            first_name: user.first_name || lg.first_name || "",
            middle_initial: user.middle_initial || lg.middle_initial || "",
            last_name: user.last_name || lg.last_name || "",
            username: user.username || "",
            pro_title: user.pro_title || "",
        });
        setContacts(user.contacts && user.contacts.length ? user.contacts : []);
        setEditing(true);
    };

    const save = async () => {
        if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First and last name are required"); return; }
        if ((form.username || "").trim().length < 3) { toast.error("Username must be at least 3 characters"); return; }
        const cleanedContacts = contacts.map((c) => ({ type: c.type, value: (c.value || "").trim() })).filter((c) => c.value);
        setSaving(true);
        const res = await updateIdentity({ ...form, pro_title: form.pro_title || "", contacts: cleanedContacts });
        setSaving(false);
        if (res.ok) { toast.success("Profile updated"); setEditing(false); }
        else toast.error(res.error);
    };

    if (!editing) {
        return (
            <div className="mt-6 border border-[#2a2a2a] bg-[#141414] p-4" data-testid="identity-panel">
                <div className="flex items-start justify-between gap-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
                        <Row icon={<User className="h-3.5 w-3.5 text-[#ff4500]" />} label="Name" value={user.name} testid="account-name" />
                        <Row icon={<Mail className="h-3.5 w-3.5 text-[#ff4500]" />} label="Email" value={user.email} testid="account-email" />
                        <Row label="Username" value={user.username || "—"} testid="account-username" />
                        <Row label="Active" value={PROFILE_LABEL[user.active_profile] || "Fan"} testid="account-active-profile" />
                        <Row label="Title" value={user.pro_title || "—"} testid="account-pro-title" />
                    </div>
                    <button data-testid="edit-identity-btn" onClick={open} className="shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#2a2a2a] hover:border-[#ff4500] text-[#cfcfcf] hover:text-[#ff4500] transition-colors inline-flex items-center gap-1">
                        <Edit3 className="h-3 w-3" /> Edit
                    </button>
                </div>
                <div className="mt-3 pt-3 border-t border-[#2a2a2a]" data-testid="account-contacts-summary">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-1.5">Contact / Social Media</div>
                    {user.contacts && user.contacts.length ? (
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {user.contacts.map((c, i) => (
                                <span key={i} className="text-sm text-[#cfcfcf]"><span className="text-[#8a8a8a] capitalize">{c.type}:</span> {c.value}</span>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-[#8a8a8a]">None added yet — used to claim listings. Tap Edit to add.</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="mt-6 border border-[#2a2a2a] bg-[#141414] p-4 space-y-4" data-testid="identity-panel-edit">
            <div className="text-xs uppercase tracking-[0.2em] text-[#ff4500] font-semibold">Account Info</div>
            <NameFields
                testidPrefix="identity"
                first={form.first_name}
                mi={form.middle_initial}
                last={form.last_name}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
            <div className="flex flex-wrap gap-3">
                <div className="w-[19ch]">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Username</Label>
                    <Input
                        data-testid="identity-username"
                        autoComplete="username"
                        maxLength={15}
                        value={form.username}
                        onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase().slice(0, 15) }))}
                        className="mt-1.5 h-11 rounded-none bg-[#0f0f0f] border-[#2a2a2a] w-full"
                    />
                    <p className="text-[10px] text-[#a0a0a0] mt-1 max-w-[26ch]">Up to 15 characters. Changing this updates it on all your posts.</p>
                </div>
                <div className="w-[26ch] max-w-full">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Email</Label>
                    <Input data-testid="identity-email" value={user.email} disabled className="mt-1.5 h-11 rounded-none bg-[#0a0a0a] border-[#2a2a2a] opacity-60 w-full" />
                    <p className="text-[10px] text-[#a0a0a0] mt-1">Email can't be changed.</p>
                </div>
            </div>
            <div data-testid="identity-title-block">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Title (optional)</Label>
                <div className="mt-1.5">
                    <Select value={form.pro_title || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, pro_title: v === "__none__" ? "" : v }))}>
                        <SelectTrigger
                            data-testid="identity-title-select"
                            className="h-11 w-auto min-w-0 gap-2 rounded-none bg-[#0f0f0f] border-[#2a2a2a] text-xs uppercase tracking-[0.15em] font-bold"
                            style={{ color: titleColor(form.pro_title, pro) }}
                        >
                            <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                            <SelectItem value="__none__" className="text-[#a0a0a0]">None</SelectItem>
                            {TITLES.map((t) => (
                                <SelectItem key={t} value={t} data-testid={`identity-title-opt-${t.toLowerCase()}`} style={{ color: titleColor(t, pro) }}>
                                    {t}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <p className="text-[10px] text-[#a0a0a0] mt-1">Shows next to your name. Coach, Director, Trainer & Scout are colored on Pro accounts.</p>
            </div>
            <div className="border-t border-[#2a2a2a] pt-4" data-testid="identity-contacts-block">
                <ContactsField value={contacts} onChange={setContacts} testidPrefix="identity-contact" />
                <p className="text-[10px] text-[#a0a0a0] mt-2">This name and contact info must match a listing's for you to claim it (or a listing you create that you'll then claim).</p>
            </div>
            <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-none border border-[#2a2a2a] hover:bg-transparent text-xs uppercase tracking-wider">Cancel</Button>
                <Button data-testid="identity-save" disabled={saving} onClick={save} className="rounded-none bg-[#ff4500] hover:bg-[#ff5722] text-xs uppercase tracking-wider">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
        </div>
    );
}


export default function Account() {
    const { user, loading, updateSocials } = useAuth();
    const navigate = useNavigate();
    const [socialRows, setSocialRows] = useState([]);
    const [savingSocials, setSavingSocials] = useState(false);
    const [athleteExpanded, setAthleteExpanded] = useState(false);
    const athleteRef = useRef(null);
    const hasAthlete = !!user?.birthdate;
    const openAthlete = () => {
        setAthleteExpanded(true);
        setTimeout(() => athleteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    };

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login?next=/account"); return; }
        setSocialRows(socialsToRows(user.socials));
    }, [user, loading, navigate]);

    // When arriving from the header avatar (/account?scrollTo=avatar), scroll the
    // avatar-select section into view. Team data loads async and shifts layout, so
    // keep re-centering for a short window until the position settles.
    useEffect(() => {
        if (loading || !user) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("scrollTo") !== "avatar") return;
        let tries = 0;
        let lastTop = null;
        const id = setInterval(() => {
            const el = document.querySelector('[data-testid="avatar-library-block"]');
            if (el) {
                const top = Math.round(el.getBoundingClientRect().top);
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                // Stop once the section's position has stabilized (layout finished).
                if (lastTop !== null && Math.abs(top - lastTop) < 4 && tries > 3) {
                    clearInterval(id);
                }
                lastTop = top;
            }
            if (++tries > 20) clearInterval(id);
        }, 150);
        return () => clearInterval(id);
    }, [user, loading]);

    const onSubmitSocials = async (e) => {        e.preventDefault();
        setSavingSocials(true);
        const res = await updateSocials(rowsToSocials(socialRows));
        setSavingSocials(false);
        if (res.ok) toast.success("Social links saved");
        else toast.error(res.error || "Failed to save");
    };

    if (loading || !user) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;

    return (
        <div className="max-w-4xl mx-auto px-6 py-10" data-testid="account-page">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2">Your account</div>
            <h1 className="font-display text-5xl tracking-wide" data-testid="account-page-title">Account Management</h1>

            {/* Identity strip — editable name + username (email read-only) */}
            <IdentityPanel />

            {/* Membership — Standard vs Pro (pulled from signup) */}
            <AccountTierPanel onCreateAthlete={openAthlete} hasAthlete={hasAthlete} />

            {/* Claimed listings — teams / trainer listings this user owns */}
            <ClaimedListingsPanel />

            {/* Athlete profile — free for every account (purple-outlined section).
                Before creation the entry point is the STANDARD box "+ Create" button, so
                the section only appears once expanded from there (or once a profile exists). */}
            {(hasAthlete || athleteExpanded) && (
            <div className="mt-8" data-testid="athlete-profile-section" ref={athleteRef}>
                <div className="border-2 border-[#a855f7] bg-[#a855f7]/[0.04] p-4 md:p-5">
                    <PlayerProfileForm expanded={athleteExpanded} onExpandedChange={setAthleteExpanded} hideAddButton />
                </div>
            </div>
            )}

            {/* Profile picture + tipping — available to every account type */}
            <ProfileCardPanel />

            {/* Saved searches / alerts — same data as the standalone /alerts page */}
            <div className="mt-8">
                <AlertsPanel title="Saved searches" />
            </div>

            {/* Notification preferences — global defaults + channel choice */}
            <NotificationSettingsPanel />

            {/* Appearance */}
            <AppearancePanel />
        </div>
    );
}

// -------------------- Membership tier (Standard vs Pro) --------------------
function AccountTierPanel({ onCreateAthlete, hasAthlete }) {
    const { user, addProfile, switchProfile, downgradeToStandard } = useAuth();
    const [busy, setBusy] = useState(false);
    const [confirmDown, setConfirmDown] = useState(false);
    const [cfg, setCfg] = useState({ beta_free: true, plans: [] });
    const [plan, setPlan] = useState("pro_yearly");
    const [proExpanded, setProExpanded] = useState(false);
    const current = isProUser(user) ? "pro" : "standard";

    useEffect(() => {
        api.get("/pro/config").then((r) => {
            setCfg(r.data);
            if (r.data.plans?.length) setPlan(r.data.plans[r.data.plans.length - 1].lookup_key);
        }).catch(() => {});
    }, []);

    const upgrade = async () => {
        setBusy(true);
        let last = { ok: true };
        for (const p of ["team_director", "trainer"]) {
            if (!(user.profiles || []).includes(p)) last = await addProfile(p);
        }
        await switchProfile("team_director");
        setBusy(false);
        if (last.ok) toast.success("You're on Pro — all Pro features are unlocked.");
        else toast.error(last.error || "Upgrade failed");
    };

    const checkout = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key: plan,
                origin_url: window.location.origin,
                user_id: user.id,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            setBusy(false);
            toast.error("Could not start checkout. Please try again.");
        }
    };

    const onUpgradeClick = () => checkout();

    const downgrade = async () => {
        setBusy(true);
        const res = await downgradeToStandard();
        setBusy(false);
        setConfirmDown(false);
        if (res.ok) toast.success("You're back on Standard. Your pages are now unclaimed.");
        else toast.error(res.error || "Downgrade failed");
    };

    return (
        <div className="mt-6" data-testid="account-tier-panel">
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-3">Membership</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                {ACCOUNT_TYPES.map((t) => {
                    const active = current === t;
                    const price = ACCOUNT_TYPE_PRICE[t];
                    return (
                        <div
                            key={t}
                            data-testid={`tier-card-${t}`}
                            className={`p-4 border-2 ${active ? "border-[#ff4500] bg-[#ff4500]/10" : "border-[#2a2a2a] bg-[#141414]"}`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <div className="font-display text-2xl tracking-wide">{ACCOUNT_TYPE_LABEL[t]}</div>
                                    {price.free && (
                                        <span className="text-[11px] uppercase tracking-[0.2em] text-[#22c55e]" data-testid="standard-free-label">Free</span>
                                    )}
                                    {t === "pro" && (
                                        <span className="text-[11px] tracking-[0.06em] text-[#22c55e]" data-testid="pro-free-label">FREE now while in public beta</span>
                                    )}
                                </div>
                                {active && (
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] border border-[#ff4500] px-2 py-0.5">Current</span>
                                )}
                            </div>
                            <div className="text-xs text-[#a0a0a0] mt-1">{ACCOUNT_TYPE_TAGLINE[t]}</div>
                            <ul className="mt-3 space-y-1.5">
                                {ACCOUNT_TYPE_FEATURES[t].map((f, i) => {
                                    const soon = f.endsWith("*");
                                    const text = soon ? f.slice(0, -1) : f;
                                    const isIntro = f.endsWith(":");
                                    const isAthlete = t === "standard" && text.toLowerCase().includes("athlete profile");
                                    const parts = isAthlete ? text.split(/athlete profile/i) : null;
                                    return (
                                        <li key={i} className={`text-[11px] leading-snug ${isIntro ? "text-[#cfcfcf] font-semibold" : "text-[#a0a0a0]"} flex gap-1.5`}>
                                            {!isIntro && <Check className="h-3 w-3 text-[#ff4500] shrink-0 mt-0.5" />}
                                            <span>
                                                {isAthlete ? (
                                                    <>{parts[0]}<span className="uppercase font-semibold text-[#c084fc]">Athlete Profile</span>{parts.slice(1).join("athlete profile")}</>
                                                ) : (
                                                    <>{text}{soon && <span className="text-[#ff4500]"> *</span>}</>
                                                )}
                                                {isAthlete && !hasAthlete && (
                                                    <button
                                                        type="button"
                                                        data-testid="standard-create-athlete-btn"
                                                        onClick={onCreateAthlete}
                                                        className="ml-2 inline-flex items-center gap-1 align-middle border border-[#a855f7] text-[#c084fc] hover:bg-[#a855f7]/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-semibold transition-colors rounded-none"
                                                    >
                                                        <Plus className="h-3 w-3" /> Create
                                                    </button>
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                            {t === "pro" && (
                                <button
                                    type="button"
                                    data-testid="pro-more-info-toggle"
                                    onClick={() => setProExpanded((v) => !v)}
                                    className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white transition-colors"
                                >
                                    {proExpanded ? (<><ChevronUp className="h-3.5 w-3.5" /> Collapse info</>) : (<><ChevronDown className="h-3.5 w-3.5" /> More info</>)}
                                </button>
                            )}
                            {t === "pro" && proExpanded && current === "standard" && (
                                <div className="mt-4" data-testid="pro-plans">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">
                                        Choose a plan
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {(cfg.plans || []).map((pl) => {
                                            const on = plan === pl.lookup_key;
                                            return (
                                                <button
                                                    key={pl.lookup_key}
                                                    type="button"
                                                    data-testid={`plan-${pl.lookup_key}`}
                                                    onClick={() => setPlan(pl.lookup_key)}
                                                    className={`p-2 border text-center transition-colors ${on ? "border-[#ff4500] bg-[#ff4500]/10" : "border-[#2a2a2a] bg-[#0f0f0f] hover:border-[#3a3a3a]"}`}
                                                >
                                                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0]">{pl.label}</div>
                                                    <div className="font-display text-xl tracking-wide">{pl.price}</div>
                                                    <div className="text-[9px] text-[#a0a0a0]">{pl.cadence}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-3 text-[11px] leading-snug text-[#22c55e]" data-testid="pro-beta-notice">
                                        {cfg.beta_free ? "Sign up during our public beta and get 90 days free — add a card now and you won't be charged for 90 days. After that you'll be billed for the plan you selected. Cancel anytime." : BETA_NOTICE}
                                    </div>
                                    <Button
                                        type="button"
                                        data-testid="upgrade-to-pro-btn"
                                        disabled={busy}
                                        onClick={onUpgradeClick}
                                        className="mt-3 w-full h-11 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold"
                                    >
                                        {busy ? "Please wait…" : (cfg.beta_free ? "Start Pro — add card (free in beta)" : "Upgrade to Pro")}
                                    </Button>
                                </div>
                            )}
                            {t === "pro" && proExpanded && current === "pro" && (
                                <div className="mt-4" data-testid="pro-active-note">
                                    <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#22c55e]">
                                        <Check className="h-3.5 w-3.5" /> You're on Pro
                                    </div>
                                    {!confirmDown ? (
                                        <button
                                            type="button"
                                            data-testid="downgrade-open"
                                            onClick={() => setConfirmDown(true)}
                                            className="mt-3 block text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white underline underline-offset-4"
                                        >
                                            Downgrade to Standard
                                        </button>
                                    ) : (
                                        <div className="mt-3 border border-red-500/50 bg-red-500/10 p-3" data-testid="downgrade-confirm">
                                            <div className="text-[11px] leading-snug text-red-300">
                                                Downgrading un-claims your team, trainer and event pages. You'll lose moderation of those pages and the ability to respond to reviews, and any moderators you assigned will be removed. You can re-upgrade anytime.
                                            </div>
                                            <div className="mt-3 flex gap-2">
                                                <Button
                                                    type="button"
                                                    data-testid="downgrade-confirm-btn"
                                                    disabled={busy}
                                                    onClick={downgrade}
                                                    className="h-9 px-3 rounded-none bg-red-600 hover:bg-red-700 text-white uppercase tracking-[0.2em] text-[10px] font-semibold"
                                                >
                                                    {busy ? "Downgrading…" : "Yes, downgrade"}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    data-testid="downgrade-cancel-btn"
                                                    onClick={() => setConfirmDown(false)}
                                                    className="h-9 px-3 rounded-none bg-transparent border border-[#2a2a2a] text-[#a0a0a0] hover:text-white uppercase tracking-[0.2em] text-[10px]"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// -------------------- Team Director / Coach settings --------------------
// Derive the base "program" name for a team by stripping the auto-appended
// " <Boys|Girls> <age|grade|grad_year>" suffix produced by the inline multi-add flow.
// Falls back to the raw team.name when no suffix is present.
function baseProgramName(team) {
    if (!team || !team.name) return "";
    const g = team.gender === "F" ? "Girls" : "Boys";
    const id = team.age_group || team.grade || team.grad_year || "";
    if (id) {
        const suffix = ` ${g} ${id}`;
        if (team.name.endsWith(suffix)) {
            return team.name.slice(0, -suffix.length).trim();
        }
    }
    return team.name.trim();
}

function TeamDirectorSettings() {
    const { sport } = useSport();
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingProgramBase, setEditingProgramBase] = useState(null);
    const [adminProgramBase, setAdminProgramBase] = useState(null);
    // Sort controls for the roster list (applies within each program group).
    const [sortBy, setSortBy] = useState("age_group"); // age_group | grade | grad_year | gender
    const [sortDir, setSortDir] = useState("asc");     // asc | desc

    const reload = () => {
        setLoading(true);
        api.get("/my-teams")
            .then((r) => setTeams(r.data))
            .catch(() => setTeams([]))
            .finally(() => setLoading(false));
    };
    useEffect(reload, []);

    // Group teams by base program name so multi-age rosters render under one heading.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const t of teams) {
            const base = baseProgramName(t) || "Team";
            if (!map.has(base)) map.set(base, []);
            map.get(base).push(t);
        }
        // Sort teams inside each group.
        const gradeOrder = ["K", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"];
        const cmp = (a, b) => {
            let va, vb;
            if (sortBy === "age_group") {
                va = parseInt(a.age_group) || 999;
                vb = parseInt(b.age_group) || 999;
            } else if (sortBy === "grade") {
                const ia = gradeOrder.indexOf(a.grade);
                const ib = gradeOrder.indexOf(b.grade);
                va = ia === -1 ? 999 : ia;
                vb = ib === -1 ? 999 : ib;
            } else if (sortBy === "grad_year") {
                va = parseInt(a.grad_year) || 0;
                vb = parseInt(b.grad_year) || 0;
            } else { // gender
                va = a.gender === "F" ? 1 : 0;
                vb = b.gender === "F" ? 1 : 0;
            }
            return sortDir === "asc" ? va - vb : vb - va;
        };
        for (const [, list] of map) list.sort(cmp);
        return Array.from(map.entries()); // [[baseName, teams[]], ...]
    }, [teams, sortBy, sortDir]);

    // Contextual direction labels — swap wording so it reads naturally per field.
    const dirLabels = useMemo(() => {
        if (sortBy === "grad_year") return { asc: "Oldest class first", desc: "Youngest class first" };
        if (sortBy === "age_group" || sortBy === "grade") return { asc: "Youngest first", desc: "Oldest first" };
        return { asc: "Boys first", desc: "Girls first" };
    }, [sortBy]);

    return (
        <div data-testid="team-director-settings" className="space-y-4">
            <div className="border border-[#2a2a2a] bg-[#141414] p-4">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div>
                        <div className="font-display text-xl tracking-wide">Your teams</div>
                        <div className="text-[11px] text-[#a0a0a0]">
                            Add one program name per team, then attach age groups, grades, or graduation years.
                            Each account can have up to <strong className="text-white">10 team names</strong>.
                            <span className="ml-1 text-[#a0a0a0]">
                                ({grouped.length}/10 used)
                            </span>
                        </div>
                    </div>
                    {!showAddForm && !editingProgramBase && (
                        <Button
                            type="button"
                            data-testid="director-show-add-team"
                            onClick={() => {
                                if (grouped.length >= 10) {
                                    toast.error("You've reached the 10 team-name limit. Delete a program or contact support.");
                                    return;
                                }
                                setShowAddForm(true);
                            }}
                            disabled={grouped.length >= 10}
                            className="h-9 px-3 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-[10px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus className="h-3 w-3 mr-1.5" /> Add new team name
                        </Button>
                    )}
                </div>

                {showAddForm && (
                    <InlineAddTeamForm
                        sport={sport}
                        onCancel={() => setShowAddForm(false)}
                        onCreated={() => { setShowAddForm(false); reload(); }}
                    />
                )}

                {!loading && teams.length > 1 && (
                    <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="team-sort-controls">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Sort by</span>
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger data-testid="team-sort-by" className="h-8 w-[140px] rounded-none bg-[#0f0f0f] border-[#2a2a2a] text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none bg-[#141414] border-[#2a2a2a]">
                                <SelectItem value="age_group">Age Group</SelectItem>
                                <SelectItem value="grade">Grade</SelectItem>
                                <SelectItem value="grad_year">Grad Year</SelectItem>
                                <SelectItem value="gender">Gender</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={sortDir} onValueChange={setSortDir}>
                            <SelectTrigger data-testid="team-sort-dir" className="h-8 w-[180px] rounded-none bg-[#0f0f0f] border-[#2a2a2a] text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none bg-[#141414] border-[#2a2a2a]">
                                <SelectItem value="asc">{dirLabels.asc}</SelectItem>
                                <SelectItem value="desc">{dirLabels.desc}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {loading ? (
                    <div className="text-sm text-[#a0a0a0]">Loading your teams…</div>
                ) : teams.length === 0 && !showAddForm ? (
                    <div className="text-sm text-[#a0a0a0]" data-testid="director-teams-empty">
                        No teams yet. Add your first age-group team to appear in the directory.
                    </div>
                ) : teams.length > 0 && (
                    <div className={`space-y-6 ${showAddForm ? "mt-3" : ""}`}>
                        {grouped.map(([baseName, groupTeams]) => (
                            <div key={baseName} data-testid={`team-group-${baseName}`}>
                                {editingProgramBase === baseName ? (
                                    <InlineAddTeamForm
                                        sport={sport}
                                        existing={{ base_name: baseName, rosters: groupTeams }}
                                        onCancel={() => setEditingProgramBase(null)}
                                        onCreated={() => { setEditingProgramBase(null); reload(); }}
                                    />
                                ) : (
                                    <>
                                        <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                                            <div className="font-display text-base tracking-wide text-white">
                                                Your{" "}
                                                <Link
                                                    to={`/teams/${groupTeams[0].id}`}
                                                    data-testid={`program-link-${baseName}`}
                                                    className="text-[#ff4500] hover:underline"
                                                >
                                                    {baseName}
                                                </Link>{" "}
                                                teams
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    type="button"
                                                    data-testid={`add-administrator-${baseName}`}
                                                    onClick={() => setAdminProgramBase(adminProgramBase === baseName ? null : baseName)}
                                                    className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline inline-flex items-center gap-1"
                                                >
                                                    <Shield className="h-3 w-3" /> {adminProgramBase === baseName ? "Hide admins" : "+ Add team admin"}
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid={`update-program-${baseName}`}
                                                    onClick={() => setEditingProgramBase(baseName)}
                                                    className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline inline-flex items-center gap-1"
                                                >
                                                    <Edit3 className="h-3 w-3" /> Update {baseName} info
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid={`delete-program-${baseName}`}
                                                    onClick={async () => {
                                                        const ok = window.confirm(
                                                            `Delete the entire "${baseName}" program?\n\nThis will remove all ${groupTeams.length} team${groupTeams.length === 1 ? "" : "s"} and any reviews. This cannot be undone.`
                                                        );
                                                        if (!ok) return;
                                                        try {
                                                            await api.delete(`/teams/${groupTeams[0].id}/program`);
                                                            toast.success(`${baseName} deleted`);
                                                            reload();
                                                        } catch (err) {
                                                            toast.error(err?.response?.data?.detail || "Delete failed");
                                                        }
                                                    }}
                                                    className="text-[10px] uppercase tracking-[0.2em] text-[#ff3b3b] hover:underline inline-flex items-center gap-1"
                                                >
                                                    <Trash2 className="h-3 w-3" /> Delete all {baseName} teams
                                                </button>
                                            </div>
                                        </div>
                                        {adminProgramBase === baseName && (
                                            <div className="mt-3" data-testid={`administrators-panel-${baseName}`}>
                                                <AdministratorsManager
                                                    entityType="team"
                                                    entityId={groupTeams[0].id}
                                                    accent="#ff4500"
                                                    canManage
                                                    embedded
                                                    showPublicEdit
                                                    publicEdit={!!groupTeams[0].allow_public_edit}
                                                    onTogglePublicEdit={async (val) => {
                                                        try {
                                                            await api.patch(`/teams/${groupTeams[0].id}/program`, { allow_public_edit: val });
                                                            toast.success(val ? "Public editing enabled" : "Public editing disabled");
                                                            reload();
                                                        } catch (err) {
                                                            toast.error(err?.response?.data?.detail || "Could not update");
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <ProgramLogoUploader
                                            baseName={baseName}
                                            representativeId={groupTeams[0].id}
                                            currentLogo={groupTeams[0].logo}
                                            onSaved={reload}
                                        />
                                        <div className="space-y-2 mt-3">
                                            {groupTeams.map((t) => (
                                                <TeamCoachRow
                                                    key={t.id}
                                                    team={t}
                                                    hideName
                                                    siblings={groupTeams}
                                                    baseName={baseName}
                                                    editing={editingId === t.id}
                                                    onEdit={() => setEditingId(t.id)}
                                                    onCancel={() => setEditingId(null)}
                                                    onSaved={() => { setEditingId(null); reload(); }}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Inline team form — flexible flow supporting multi-age/grade programs.
//
// Two modes:
//   - Create (existing = undefined): user picks a program name, ZIP, age/grade/grad-year
//     multi-selects, director info, etc. Submits N POST /teams calls.
//   - Edit (existing = { base_name, rosters }): pre-fills the form with the program's
//     current name/ZIP/city. New identifier picks (age/grade/grad-year) create new
//     sibling teams; changes to shared fields (name / ZIP / city / circuit) PATCH every
//     existing sibling via PATCH /teams/{id}/program.
export function InlineAddTeamForm({ sport, onCancel, onCreated, existing, variant = "modal" }) {
    const { user } = useAuth();
    const editMode = !!existing;
    const isPage = variant === "page";
    const rep = editMode ? existing.rosters[0] : null;

    // In edit mode, pre-fill the age/grade/grad-year multi-selects with whatever the
    // program already covers for the currently-selected gender. This lets the director
    // SEE their existing teams as selected and toggle new ones on/off from the same UI.
    const initialGenders = editMode
        ? (() => {
            const gs = [...new Set(existing.rosters.map((r) => (r.gender === "F" ? "F" : "M")))];
            return gs.length ? gs : ["M"];
        })()
        : ["M"];
    const initialCircuits = editMode
        ? (() => {
            const set = [];
            for (const r of existing.rosters) {
                const list = r.circuits && r.circuits.length ? r.circuits : (r.circuit ? [r.circuit] : []);
                for (const c of list) if (c && !set.includes(c)) set.push(c);
            }
            return set.length ? [set[0]] : ["AAU"];
        })()
        : ["AAU"];

    const [name, setName] = useState(editMode ? existing.base_name : "");
    const [genders, setGenders] = useState(initialGenders);
    const [circuits, setCircuits] = useState(initialCircuits);
    // In edit mode, pre-select the age/grade/grad-year pickers with the program's EXISTING
    // teams (per gender) so directors see them selected. Selecting a new value adds a team;
    // DESELECTING an existing value deletes that team (with a confirm prompt) on save.
    const idsForGender = (g) => {
        if (!editMode) return { ages: [], grades: [], gradYears: [] };
        const f = existing.rosters.filter((r) => (r.gender === "F" ? "F" : "M") === g);
        return {
            ages: [...new Set(f.map((r) => r.age_group).filter(Boolean))],
            grades: [...new Set(f.map((r) => r.grade).filter(Boolean))],
            gradYears: [...new Set(f.map((r) => r.grad_year).filter(Boolean))],
        };
    };
    const [selByGender, setSelByGender] = useState({ M: idsForGender("M"), F: idsForGender("F") });
    const setSel = (g, field, val) => setSelByGender((p) => ({ ...p, [g]: { ...p[g], [field]: val } }));
    // Existing teams the director chose to remove (by deselecting) — deleted on save.
    const [toDelete, setToDelete] = useState([]);
    // Find the existing roster for a given gender + dimension + identifier value.
    const dimField = { ages: "age_group", grades: "grade", gradYears: "grad_year" };
    const findExistingRoster = (g, field, val) =>
        editMode ? existing.rosters.find((r) => (r.gender === "F" ? "F" : "M") === g && (r[dimField[field]] || "") === val) : null;
    // Wrap picker changes to confirm deletion when an EXISTING team is deselected.
    const handleSelChange = (g, field, newVals) => {
        const oldVals = (selByGender[g] || {})[field] || [];
        const removed = oldVals.filter((v) => !newVals.includes(v));
        const gl = g === "F" ? "Girls" : "Boys";
        const removedExisting = removed.map((v) => ({ v, r: findExistingRoster(g, field, v) })).filter((x) => x.r);
        if (removedExisting.length) {
            const names = removedExisting.map((x) => `${gl} ${x.v}`).join(", ");
            if (!window.confirm(`Delete ${names}? This permanently removes the team and its info when you save. This cannot be undone.`)) {
                return; // keep selection unchanged
            }
            setToDelete((prev) => [...new Set([...prev, ...removedExisting.map((x) => x.r.id)])]);
        }
        // If a value was re-added, drop it from the delete queue.
        const added = newVals.filter((v) => !oldVals.includes(v));
        if (added.length) {
            const readdIds = added.map((v) => findExistingRoster(g, field, v)).filter(Boolean).map((r) => r.id);
            if (readdIds.length) setToDelete((prev) => prev.filter((id) => !readdIds.includes(id)));
        }
        setSel(g, field, newVals);
    };
    const [zip, setZip] = useState(editMode ? (rep?.zip || "") : "");
    const [city, setCity] = useState(editMode ? (rep?.city || "") : "");
    const [state, setState] = useState(editMode ? (rep?.state || "") : "");
    const [zipLookupError, setZipLookupError] = useState("");
    const [zipBusy, setZipBusy] = useState(false);
    // Director info (used for every team by default). Name split into First/M.I./Last.
    // New teams prefill the director name from the account's profile to save time (editable).
    const [director, setDirector] = useState(() => {
        if (editMode) {
            const s = splitName(rep?.coach || "");
            return { ...s, email: rep?.contact_email || "", phone: rep?.contact_phone || "" };
        }
        const s = splitName(user?.name || "");
        return { ...s, email: user?.email || "", phone: "" };
    });
    // Optional per-team Head Coach override for NEW teams: { "Boys 14U": {coach,email,phone}, ... }
    const [selectedTeamKey, setSelectedTeamKey] = useState("");
    const [overrides, setOverrides] = useState({});
    // Inline edits to EXISTING teams' head coach info + gender/identifier, keyed by team id.
    const [teamEdits, setTeamEdits] = useState(() => {
        if (!editMode) return {};
        const acc = {};
        for (const r of existing.rosters) {
            const s = splitName(r.coach || "");
            const dim = r.age_group ? "age" : r.grade ? "grade" : r.grad_year ? "gradyear" : "age";
            acc[r.id] = {
                first_name: s.first_name, middle_initial: s.middle_initial, last_name: s.last_name,
                contact_email: r.contact_email || "", contact_phone: r.contact_phone || "",
                gender: r.gender === "F" ? "F" : "M",
                dim,
                identifier: r.age_group || r.grade || r.grad_year || "",
            };
        }
        return acc;
    });
    const setTeamEditField = (id, field, val) => setTeamEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: val } }));
    const patchTeamEdit = (id, patch) => setTeamEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
    // Existing teams with no head coach can be expanded into an editable row on demand.
    const [expandedIds, setExpandedIds] = useState([]);
    // Sort order for the teams shown in the edit form.
    const [teamSortMode, setTeamSortMode] = useState(DEFAULT_SORT_STATE);
    // Program media/branding fields — apply to every team in the program.
    const [website, setWebsite] = useState(editMode ? (rep?.website || "") : "");
    const [description, setDescription] = useState(editMode ? (rep?.description || "") : "");
    const [photo, setPhoto] = useState(editMode ? (rep?.photo || "") : "");
    const [logo, setLogo] = useState(editMode ? (rep?.logo || "") : "");
    const [socialLinks, setSocialLinks] = useState(
        editMode && Array.isArray(rep?.social_media_links) && rep.social_media_links.length ? rep.social_media_links : [""]
    );
    const MAX_SOCIAL_LINKS = 10;
    const addSocial = () => {
        if (socialLinks.length >= MAX_SOCIAL_LINKS) { toast.info(`You can add up to ${MAX_SOCIAL_LINKS} links`); return; }
        setSocialLinks([...socialLinks, ""]);
    };
    const updateSocial = (idx, val) => setSocialLinks(socialLinks.map((v, i) => (i === idx ? val : v)));
    const removeSocial = (idx) => {
        const next = socialLinks.filter((_, i) => i !== idx);
        setSocialLinks(next.length ? next : [""]);
    };
    const [saving, setSaving] = useState(false);

    const ageOpts = agesForSport(sport);
    const circuitOpts = circuitsForSport(sport);
    const gradYears_available = gradYearOptions();

    // Program-level gender + team helpers.
    const selectedGenders = ["M", "F"].filter((g) => genders.includes(g));
    const bothGenders = selectedGenders.length > 1;
    const genderLabel = (g) => (g === "F" ? "Girls" : "Boys");
    // Each selected age / grade / grad-year is its OWN team (they are separate teams).
    const buildBase = (g) => {
        const { ages: a, grades: gr, gradYears: gy } = selByGender[g] || { ages: [], grades: [], gradYears: [] };
        const out = [];
        a.forEach((x) => out.push({ age_group: x, grade: "", grad_year: "" }));
        gr.forEach((x) => out.push({ age_group: "", grade: x, grad_year: "" }));
        gy.forEach((x) => out.push({ age_group: "", grade: "", grad_year: x }));
        return out;
    };
    const teamKeyFor = (g, t) => {
        const id = t.age_group || t.grade || t.grad_year || "";
        return `${genderLabel(g)} ${id}`.trim();
    };
    const rosterLabelFor = (r) => {
        const id = r.age_group || r.grade || r.grad_year || "";
        return id ? `${genderLabel(r.gender)} ${id}` : genderLabel(r.gender);
    };
    const joinName = (p) => {
        const mi = p && p.middle_initial ? `${p.middle_initial}. ` : "";
        return `${((p && p.first_name) || "").trim()} ${mi}${((p && p.last_name) || "").trim()}`.trim();
    };
    // Every team the current selections describe (across both genders).
    const allBase = selectedGenders.flatMap((g) => buildBase(g).map((t) => ({ ...t, gender: g })));
    // Teams that already exist in the program (edit mode) keyed by composite label.
    const existingKeys = new Set(editMode ? existing.rosters.map((r) => teamKeyFor(r.gender, r)) : []);
    // NEW teams to be created = selected teams that don't already exist.
    const newTeams = allBase.filter((t) => !existingKeys.has(teamKeyFor(t.gender, t)));

    // The program is "multi" when it will contain more than one team overall.
    const totalTeamCount = (editMode ? existing.rosters.length : 0) + newTeams.length;
    const isMulti = totalTeamCount > 1;
    const coachLabel = isMulti ? "Director" : "Head Coach";
    const directorName = () => joinName(director);

    // Dropdown to add Head Coach info — NEW teams + existing teams that have no coach yet.
    const editableRosters = editMode
        ? sortRosters(existing.rosters.filter((r) => hasCoach(r) || expandedIds.includes(r.id)), teamSortMode)
        : [];
    const collapsedRosters = editMode
        ? sortRosters(existing.rosters.filter((r) => !hasCoach(r) && !expandedIds.includes(r.id)), teamSortMode)
        : [];
    // Map composite key -> existing uncoached roster id, so the dropdown can expand it.
    const collapsedKeyToId = {};
    collapsedRosters.forEach((r) => { collapsedKeyToId[teamKeyFor(r.gender, r)] = r.id; });
    const teamOptions = [
        ...collapsedRosters.map((r) => { const k = teamKeyFor(r.gender, r); return [k, k]; }),
        ...newTeams.map((t) => { const k = teamKeyFor(t.gender, t); return [k, k]; }),
    ];
    const identifierOptions = (dim) => {
        if (dim === "grade") return GRADES.map((x) => [x, x]);
        if (dim === "gradyear") return gradYears_available.map((y) => [y, y]);
        return ageOpts.map((a) => [a, a]);
    };
    const onPickTeamToCoach = (key) => {
        const rid = collapsedKeyToId[key];
        if (rid) {
            setExpandedIds((p) => (p.includes(rid) ? p : [...p, rid]));
            setSelectedTeamKey("");
        } else {
            setSelectedTeamKey(key);
        }
    };

    // Fetch city/state whenever a 5-digit ZIP is entered.
    useEffect(() => {
        const z = (zip || "").trim();
        setZipLookupError("");
        if (!z || z.length !== 5 || !/^\d{5}$/.test(z)) {
            setCity("");
            setState("");
            return;
        }
        let cancelled = false;
        setZipBusy(true);
        api.get(`/zip-lookup`, { params: { zip: z } })
            .then((r) => {
                if (cancelled) return;
                setCity(r.data.city || "");
                setState(r.data.state || "");
            })
            .catch(() => {
                if (cancelled) return;
                setCity("");
                setState("");
                setZipLookupError("Could not locate that ZIP");
            })
            .finally(() => { if (!cancelled) setZipBusy(false); });
        return () => { cancelled = true; };
    }, [zip]);

    // If the user narrows their age/grade selection back to <=1 wipe out the overrides.
    useEffect(() => {
        if (!isMulti) {
            setOverrides({});
            setSelectedTeamKey("");
        }
    }, [isMulti]);

    const setOverrideField = (key, field, value) => {
        setOverrides((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || {}), [field]: value },
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error("Team name is required"); return; }
        if (!zip.trim() || !city || !state) { toast.error("A valid ZIP is required"); return; }
        if (!circuits.length) { toast.error("Pick at least one Circuit"); return; }
        if (!selectedGenders.length) { toast.error("Pick at least one Gender"); return; }
        if (!editMode && allBase.length === 0) {
            toast.error("Pick at least one Age Group, Grade, or Graduation Year");
            return;
        }
        setSaving(true);
        const cleanSocial = socialLinks.map((u) => u.trim()).filter(Boolean);
        const singleNew = !editMode && totalTeamCount === 1;
        try {
            // ---------- Edit mode: PATCH shared program fields, then per-team edits ----------
            if (editMode) {
                const rep = existing.rosters[0];
                const patch = {};
                if (name.trim() !== existing.base_name) patch.name = name.trim();
                if ((city || "").trim() !== (rep.city || "")) patch.city = (city || "").trim();
                if ((zip || "").trim() !== (rep.zip || "")) patch.zip = (zip || "").trim();
                if (JSON.stringify(circuits) !== JSON.stringify(initialCircuits)) patch.circuits = circuits;
                if ((website || "").trim() !== (rep.website || "")) patch.website = (website || "").trim();
                if ((description || "") !== (rep.description || "")) patch.description = description || "";
                if ((photo || "") !== (rep.photo || "")) patch.photo = photo || "";
                if (JSON.stringify(cleanSocial) !== JSON.stringify(rep.social_media_links || [])) patch.social_media_links = cleanSocial;
                if ((logo || "") !== (rep.logo || "")) patch.logo = logo || "";
                if (Object.keys(patch).length) {
                    await api.patch(`/teams/${rep.id}/program`, patch);
                }
                const multiExisting = existing.rosters.length > 1;
                // Save inline head coach edits + gender/identifier changes on each existing team.
                for (const r of existing.rosters) {
                    if (toDelete.includes(r.id)) continue; // queued for deletion below
                    const ed = teamEdits[r.id] || {};
                    const teamPatch = {};
                    const edCoach = joinName(ed);
                    if (edCoach !== (r.coach || "")) teamPatch.coach = edCoach;
                    if ((ed.contact_email || "").trim() !== (r.contact_email || "")) teamPatch.contact_email = (ed.contact_email || "").trim();
                    if ((ed.contact_phone || "").trim() !== (r.contact_phone || "")) teamPatch.contact_phone = (ed.contact_phone || "").trim();
                    // Gender / identifier change → rename the team.
                    const newGender = ed.gender || (r.gender === "F" ? "F" : "M");
                    const curId = r.age_group || r.grade || r.grad_year || "";
                    const newId = ed.identifier != null ? ed.identifier : curId;
                    if (newGender !== (r.gender === "F" ? "F" : "M") || newId !== curId) {
                        teamPatch.gender = newGender;
                        teamPatch.age_group = ed.dim === "age" ? newId : "";
                        teamPatch.grade = ed.dim === "grade" ? newId : "";
                        teamPatch.grad_year = ed.dim === "gradyear" ? newId : "";
                        const gl = newGender === "F" ? "Girls" : "Boys";
                        teamPatch.name = multiExisting && newId ? `${name.trim()} ${gl} ${newId}` : name.trim();
                    }
                    if (Object.keys(teamPatch).length) {
                        await api.put(`/teams/${r.id}`, teamPatch);
                    }
                }
            }

            // ---------- Create the NEW teams (across both genders) ----------
            const toCreate = newTeams;
            const totalAfter = (editMode ? existing.rosters.length : 0) + toCreate.length;
            const multi = totalAfter > 1;

            for (const t of toCreate) {
                const g = t.gender;
                const gl = genderLabel(g);
                const key = teamKeyFor(g, t);
                const ov = overrides[key] || {};
                // Head coach is optional per team — only a single brand-new team inherits the panel value.
                const coach = joinName(ov) || (singleNew ? directorName() : "");
                const contact_email = (ov.email && ov.email.trim()) || (singleNew ? director.email : "");
                const contact_phone = (ov.phone && ov.phone.trim()) || (singleNew ? director.phone : "");
                const identifier = t.age_group || t.grade || t.grad_year || "";
                const teamName = multi && identifier
                    ? `${name.trim()} ${gl} ${identifier}`
                    : name.trim();
                await api.post("/teams", {
                    name: teamName,
                    sport,
                    age_group: t.age_group,
                    grade: t.grade,
                    grad_year: t.grad_year,
                    gender: g,
                    circuit: circuits[0],
                    circuits,
                    city,
                    state,
                    zip: zip.trim(),
                    coach,
                    contact_email,
                    contact_phone,
                    website: (website || "").trim(),
                    description: description || "",
                    photo: photo || "",
                    logo: logo || "",
                    social_media_links: cleanSocial,
                    // Adding to an existing program: inherit its owner so the new roster
                    // groups with it instead of creating a duplicate program entry.
                    ...(editMode && existing?.rosters?.[0]?.id ? { program_team_id: existing.rosters[0].id } : {}),
                });
            }
            const base = toCreate;

            // ---------- Delete any teams the director deselected ----------
            for (const id of toDelete) {
                await api.delete(`/teams/${id}`);
            }

            if (editMode) {
                toast.success(`${name} updated${base.length ? ` — ${base.length} new team${base.length === 1 ? "" : "s"} added` : ""}`);
            } else {
                toast.success(`${name} added — ${base.length} team${base.length === 1 ? "" : "s"} created`);
            }
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || (editMode ? "Failed to update program" : "Failed to add team"));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className={isPage ? "space-y-5" : "border border-[#ff4500] bg-[#0f0f0f] p-3 space-y-3"} data-testid={editMode ? "edit-program-form" : "inline-add-team-form"}>
            {!isPage && (
                <div className="flex items-center justify-between">
                    <div className="font-display text-lg tracking-wide">
                        {editMode ? `Update ${existing.base_name} · ${sport}` : `New team · ${sport}`}
                    </div>
                    <button type="button" onClick={onCancel} className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white">Cancel</button>
                </div>
            )}

            <SmallField label={editMode ? "Program Name" : "Team Name"} testid="new-team-name" value={name} onChange={setName} required />

            {/* Program-level Circuit + Gender (both multi-select) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Circuit <span className="text-[#ff4500]">*</span></Label>
                    <div className="mt-1">
                        <CircuitSelect sport={sport} multi={false} value={circuits[0] || ""} onChange={(v) => setCircuits(v ? [v] : [])} testid="new-team-circuit" />
                    </div>
                </div>
                <div>
                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Gender <span className="text-[#ff4500]">*</span></Label>
                    <div className="mt-1">
                        <MultiSelect testid="new-team-gender" label="Genders" value={genders} onChange={setGenders} options={[["M", "Boys"], ["F", "Girls"]]} />
                    </div>
                </div>
            </div>

            {/* Age + Grade + Grad Year — one set per selected gender */}
            {selectedGenders.map((g) => (
                <div key={g} className="space-y-1" data-testid={`age-grade-group-${g}`}>
                    {bothGenders && (
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff4500]" data-testid={`age-grade-group-label-${g}`}>
                            {genderLabel(g)} Teams
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                            <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{bothGenders ? `${genderLabel(g)} Age Groups` : "Age Groups"}</Label>
                            <div className="mt-1">
                                <MultiSelect testid={`new-team-ages-${g}`} label="Ages" value={(selByGender[g] || {}).ages || []} onChange={(v) => handleSelChange(g, "ages", v)} options={ageOpts.map((a) => [a, a])} />
                            </div>
                        </div>
                        <div>
                            <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{bothGenders ? `${genderLabel(g)} Grades` : "Grades"}</Label>
                            <div className="mt-1">
                                <MultiSelect testid={`new-team-grades-${g}`} label="Grades" value={(selByGender[g] || {}).grades || []} onChange={(v) => handleSelChange(g, "grades", v)} options={GRADES.map((x) => [x, x])} />
                            </div>
                        </div>
                        <div>
                            <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{bothGenders ? `${genderLabel(g)} Graduation Year` : "Graduation Year"}</Label>
                            <div className="mt-1">
                                <MultiSelect testid={`new-team-grad-years-${g}`} label="Grad Years" value={(selByGender[g] || {}).gradYears || []} onChange={(v) => handleSelChange(g, "gradYears", v)} options={gradYears_available.map((y) => [y, y])} />
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <div className="text-[10px] text-[#a0a0a0] -mt-1" data-testid="team-age-grade-note">
                {editMode
                    ? "Your current teams are pre-selected. Add a value to create a new team, or deselect one to delete that team (you'll be asked to confirm). Changes apply when you save."
                    : "Recommend choosing just age, grade, or graduation year, not a combination. Select both genders to add Boys and Girls teams at once."}
            </div>


            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
                <div>
                    <SmallField
                        label="ZIP"
                        testid="new-team-zip"
                        value={zip}
                        onChange={(v) => setZip(v.replace(/\D/g, "").slice(0, 5))}
                        required
                        maxLength={5}
                    />
                    {zipBusy && <div className="text-[9px] text-[#a0a0a0] mt-1">Looking up…</div>}
                    {zipLookupError && <div className="text-[9px] text-[#ff4500] mt-1" data-testid="zip-lookup-error">{zipLookupError}</div>}
                </div>
                <div>
                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">City <span className="text-[10px] normal-case tracking-normal text-[#666]">(you can adjust)</span></Label>
                    <Input
                        data-testid="new-team-city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={!state}
                        placeholder={state ? "" : "Enter ZIP first"}
                        className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a] disabled:opacity-60"
                        required
                    />
                </div>
                <div>
                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">State <span className="text-[10px] normal-case tracking-normal text-[#666]">(auto)</span></Label>
                    <Input
                        data-testid="new-team-state"
                        value={state}
                        readOnly
                        disabled
                        placeholder={state ? "" : "Enter ZIP first"}
                        className="mt-1 h-9 text-sm rounded-none bg-[#0a0a0a] border-[#2a2a2a] text-[#a0a0a0] cursor-not-allowed"
                    />
                </div>
            </div>

            {/* Existing teams — edit each team inline (edit mode only) */}
            {editMode && existing.rosters.length > 0 && (
                <div className="border border-[#2a2a2a] bg-[#0a0a0a]" data-testid="edit-teams-list">
                    <div className="px-3 py-2.5 border-b border-[#2a2a2a] flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0]">Teams in this program</div>
                        <div className="flex items-center gap-3 flex-wrap">
                            {existing.rosters.length > 1 && (
                                <RosterSortControls value={teamSortMode} onChange={setTeamSortMode} testidPrefix="edit-roster-sort" />
                            )}
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0]">
                                {existing.rosters.length} team{existing.rosters.length === 1 ? "" : "s"}
                            </div>
                        </div>
                    </div>
                    <div className="divide-y divide-[#2a2a2a]">
                        {editableRosters.map((r) => {
                            const ed = teamEdits[r.id] || {};
                            const dim = ed.dim || (r.age_group ? "age" : r.grade ? "grade" : "gradyear");
                            const dimLabel = dim === "grade" ? "Grade" : dim === "gradyear" ? "Grad Year" : "Age";
                            const inputCls = "mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a] placeholder:text-[#4a4a4a]";
                            const sec = `section-edit-${r.id}`;
                            return (
                                <div key={r.id} className="p-3 flex flex-wrap items-end gap-2" data-testid={`edit-roster-row-${r.id}`}>
                                    <div className="w-[92px]">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Gender</Label>
                                        <div data-testid={`edit-gender-${r.id}`} className="mt-1 h-9 flex items-center px-3 text-sm text-white bg-[#0a0a0a] border border-[#2a2a2a]">
                                            {(ed.gender || (r.gender === "F" ? "F" : "M")) === "F" ? "Girls" : "Boys"}
                                        </div>
                                    </div>
                                    <div className="w-[96px]">
                                        <SmallSelect label={dimLabel} testid={`edit-identifier-${r.id}`} value={ed.identifier != null ? ed.identifier : (r.age_group || r.grade || r.grad_year || "")} onChange={(v) => setTeamEditField(r.id, "identifier", v)} options={identifierOptions(dim)} />
                                    </div>
                                    <div className="w-[118px]">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">First</Label>
                                        <Input data-testid={`edit-coach-${r.id}-first`} autoComplete={`${sec} given-name`} name="given-name" value={ed.first_name || ""} onChange={(e) => patchTeamEdit(r.id, { first_name: e.target.value })} className={inputCls} />
                                    </div>
                                    <div className="w-[46px]">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">M.I.</Label>
                                        <Input data-testid={`edit-coach-${r.id}-mi`} autoComplete={`${sec} additional-name`} name="additional-name" maxLength={1} value={ed.middle_initial || ""} onChange={(e) => patchTeamEdit(r.id, { middle_initial: e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 1).toUpperCase() })} className={`${inputCls} text-center`} />
                                    </div>
                                    <div className="w-[124px]">
                                        <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Last</Label>
                                        <Input data-testid={`edit-coach-${r.id}-last`} autoComplete={`${sec} family-name`} name="family-name" value={ed.last_name || ""} onChange={(e) => patchTeamEdit(r.id, { last_name: e.target.value })} className={inputCls} />
                                    </div>
                                    <div className="w-[210px]">
                                        <SmallField label="Email" type="email" testid={`edit-email-${r.id}`} value={ed.contact_email || ""} onChange={(v) => setTeamEditField(r.id, "contact_email", v)} />
                                    </div>
                                    <div className="w-[150px]">
                                        <SmallField label="Phone" type="tel" testid={`edit-phone-${r.id}`} value={ed.contact_phone || ""} onChange={(v) => setTeamEditField(r.id, "contact_phone", v)} />
                                    </div>
                                </div>
                            );
                        })}
                        {["M", "F"].map((g) => {
                            const group = collapsedRosters.filter((r) => (r.gender === "F" ? "F" : "M") === g);
                            if (!group.length) return null;
                            return (
                                <div key={g} className="p-3 flex flex-col md:flex-row md:items-center gap-x-6 gap-y-1" data-testid={`edit-nocoach-${g}`}>
                                    <div className="md:w-40 md:shrink-0">
                                        <div className="font-display text-lg tracking-wide">{g === "F" ? "Girls" : "Boys"} Teams</div>
                                    </div>
                                    <div className="flex-1 text-sm text-[#cfcfcf]" data-testid={`edit-nocoach-list-${g}`}>
                                        {group.map((r) => teamIdentifier(r)).join(", ")}
                                        <span className="text-[10px] text-[#666] ml-2">— no head coach yet (add one below)</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Head Coach info — only for a single brand-new team */}
            {!editMode && totalTeamCount === 1 && (
            <div className="border border-[#2a2a2a] p-3 bg-[#0a0a0a]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2" data-testid="director-label">
                    Head Coach info <span className="normal-case tracking-normal text-[#666]">(optional)</span>
                </div>
                <div className="space-y-3">
                    <NameFields
                        testidPrefix="new-team-coach"
                        section="director"
                        required={false}
                        first={director.first_name}
                        mi={director.middle_initial}
                        last={director.last_name}
                        onChange={(patch) => setDirector((d) => ({ ...d, ...patch }))}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <SmallField label="Head Coach Email" type="email" testid="new-team-email" value={director.email} onChange={(v) => setDirector({ ...director, email: v })} />
                        <SmallField label="Head Coach Phone" type="tel" testid="new-team-phone" value={director.phone} onChange={(v) => setDirector({ ...director, phone: v })} />
                    </div>
                </div>
            </div>
            )}

            {/* Add a Head Coach to a team that has none yet (new teams + existing uncoached) */}
            {((editMode && teamOptions.length > 0) || (!editMode && isMulti && teamOptions.length > 0)) && (
                <div className="border border-dashed border-[#2a2a2a] p-3 bg-[#0a0a0a]" data-testid="team-override-panel">
                    <div className="text-[10px] text-[#a0a0a0] mb-2">
                        Optional — add a Head Coach for a team. Teams without one stay grouped in the summary above.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
                        <SmallSelect
                            label="Add Head Coach for team"
                            testid="team-override-selector"
                            value={selectedTeamKey}
                            onChange={onPickTeamToCoach}
                            options={teamOptions}
                            placeholder="Select a team…"
                        />
                    </div>
                    {selectedTeamKey && (
                        <div className="mt-3 space-y-2" data-testid={`team-override-form-${selectedTeamKey}`}>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-[#ff4500]">Head Coach · {selectedTeamKey}</div>
                            <NameFields
                                testidPrefix={`override-coach-${selectedTeamKey}`}
                                section={`override-${selectedTeamKey}`}
                                required={false}
                                first={(overrides[selectedTeamKey] || {}).first_name || ""}
                                mi={(overrides[selectedTeamKey] || {}).middle_initial || ""}
                                last={(overrides[selectedTeamKey] || {}).last_name || ""}
                                onChange={(patch) => setOverrides((prev) => ({ ...prev, [selectedTeamKey]: { ...(prev[selectedTeamKey] || {}), ...patch } }))}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <SmallField
                                    label="Head Coach Email"
                                    type="email"
                                    testid={`override-email-${selectedTeamKey}`}
                                    value={(overrides[selectedTeamKey] || {}).email || ""}
                                    onChange={(v) => setOverrideField(selectedTeamKey, "email", v)}
                                />
                                <SmallField
                                    label="Head Coach Phone"
                                    type="tel"
                                    testid={`override-phone-${selectedTeamKey}`}
                                    value={(overrides[selectedTeamKey] || {}).phone || ""}
                                    onChange={(v) => setOverrideField(selectedTeamKey, "phone", v)}
                                />
                            </div>
                        </div>
                    )}
                    {/* Show already-set overrides as compact rows */}
                    {Object.entries(overrides).filter(([, v]) => v && (joinName(v) || v.email || v.phone)).length > 0 && (
                        <div className="mt-3 space-y-1 text-[11px] text-[#e5e5e5]" data-testid="team-overrides-set">
                            {Object.entries(overrides)
                                .filter(([, v]) => v && (joinName(v) || v.email || v.phone))
                                .map(([k, v]) => (
                                    <div key={k} className="flex items-center justify-between gap-2 border border-[#2a2a2a] px-2 py-1">
                                        <div className="min-w-0 truncate">
                                            <span className="text-[#ff4500]">{k}</span> · {joinName(v) || "—"} {v.email && <>· {v.email}</>} {v.phone && <>· {v.phone}</>}
                                        </div>
                                        <button
                                            type="button"
                                            className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500]"
                                            onClick={() => setOverrides((prev) => { const next = { ...prev }; delete next[k]; return next; })}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {/* Website / Social media / Description / Photo — shared across every team in the program */}
            <div>
                <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Website</Label>
                <Input
                    data-testid="new-team-website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://…"
                    className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                />
            </div>

            <div data-testid="new-team-social-media-section">
                <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Social Media</Label>
                <div className="mt-1 space-y-2">
                    {socialLinks.map((val, i) => (
                        <div key={i} className="flex gap-2">
                            <Input
                                data-testid={`new-team-social-${i}`}
                                value={val}
                                onChange={(e) => updateSocial(i, e.target.value)}
                                placeholder={i === 0 ? "https://instagram.com/yourteam" : "https://…"}
                                className="h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                            />
                            {socialLinks.length > 1 && (
                                <button
                                    type="button"
                                    data-testid={`new-team-social-remove-${i}`}
                                    onClick={() => removeSocial(i)}
                                    aria-label="Remove link"
                                    className="h-9 w-9 border border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500 hover:text-red-500 flex items-center justify-center transition-colors"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    data-testid="new-team-social-add-btn"
                    onClick={addSocial}
                    disabled={socialLinks.length >= MAX_SOCIAL_LINKS}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 border border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:border-[#ff4500] hover:text-[#ff4500] disabled:opacity-40 disabled:hover:border-[#2a2a2a] disabled:hover:text-[#a0a0a0] transition-colors"
                >
                    <Plus className="h-3.5 w-3.5" />
                    Add Additional Social Media
                </button>
                <div className="text-[10px] text-[#666] mt-1">{socialLinks.length}/{MAX_SOCIAL_LINKS} — Instagram, TikTok, HUDL, Twitter/X, YouTube, etc.</div>
            </div>

            <div>
                <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Description</Label>
                <Textarea
                    data-testid="new-team-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="mt-1 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PhotoUpload value={logo} onChange={setLogo} aspect={1} square label="Team logo (square)" hint="Square crop · shown on cards, the map, and listings." dragPaste testid="new-team-logo-upload" />
                <PhotoUpload value={photo} onChange={setPhoto} aspect={null} label="Team photo (detail page)" hint="Free-form crop · displayed in full resolution on the team page." dragPaste testid="new-team-photo-upload" />
            </div>

            <div className="flex justify-end">
                <Button type="submit" disabled={saving} data-testid="new-team-submit" className="h-9 px-4 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-[10px] font-semibold">
                    {saving
                        ? (editMode ? "Saving…" : "Adding…")
                        : editMode
                            ? "Save changes"
                            : (isMulti ? `Add ${newTeams.length} teams` : "Add team")}
                </Button>
            </div>
        </form>
    );
}

function SmallSelect({ label, testid, value, onChange, options, placeholder, required }) {
    return (
        <div>
            <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{label}{required && <span className="text-[#ff4500] ml-0.5">*</span>}</Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testid} className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a]">
                    <SelectValue placeholder={placeholder || "Select"} />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                    {options.map(([v, lbl]) => <SelectItem key={v} value={v}>{lbl}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

// Uploader for a single program logo. On upload it POSTs the file to /api/uploads,
// then PATCHes the whole program's sibling teams so every roster picks up the new logo.
function ProgramLogoUploader({ baseName, representativeId, currentLogo, onSaved }) {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(currentLogo || "");
    useEffect(() => { setPreview(currentLogo || ""); }, [currentLogo]);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!/^image\//.test(file.type)) { toast.error("Please choose an image file"); return; }
        if (file.length && file.length > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = up.data?.url;
            if (!url) throw new Error("Upload failed");
            await api.patch(`/teams/${representativeId}/program`, { logo: url });
            setPreview(url);
            toast.success(`${baseName} logo updated`);
            onSaved && onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Logo upload failed");
        } finally { setUploading(false); }
    };

    const remove = async () => {
        setUploading(true);
        try {
            await api.patch(`/teams/${representativeId}/program`, { logo: "" });
            setPreview("");
            toast.success("Logo removed");
            onSaved && onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove logo");
        } finally { setUploading(false); }
    };

    return (
        <div className="flex items-center gap-3 border border-[#2a2a2a] p-3 bg-[#0f0f0f]" data-testid={`program-logo-${baseName}`}>
            <div className="h-14 w-14 shrink-0 bg-white flex items-center justify-center border border-[#2a2a2a] overflow-hidden">
                {preview ? (
                    <img src={preview} alt={`${baseName}`} className="h-full w-full object-contain" data-testid={`program-logo-preview-${baseName}`} />
                ) : (
                    <div className="text-[9px] uppercase tracking-[0.15em] text-[#a0a0a0] text-center leading-tight px-1">No logo</div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Program logo</div>
                <div className="text-[11px] text-[#cfcfcf]">Displays on the program card, team detail page, and map hover preview.</div>
            </div>
            <div className="flex items-center gap-2">
                <label className={`h-8 px-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] cursor-pointer border ${uploading ? "opacity-60 pointer-events-none" : ""} border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500] hover:text-white`}>
                    {uploading ? "Uploading…" : (preview ? "Replace" : "Upload logo")}
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid={`upload-logo-${baseName}`}
                        onChange={onFile}
                        disabled={uploading}
                    />
                </label>
                {preview && !uploading && (
                    <button
                        type="button"
                        data-testid={`remove-logo-${baseName}`}
                        onClick={remove}
                        className="h-8 px-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500]"
                    >
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
}


function TeamCoachRow({ team, editing, onEdit, onCancel, onSaved, hideName, siblings = [], baseName = "" }) {
    // Which multi-select differentiates this team? (age_group / grade / grad_year)
    const diffType = team.age_group ? "age_group" : team.grade ? "grade" : team.grad_year ? "grad_year" : null;
    const currentId = team.age_group || team.grade || team.grad_year || "";

    const joinName = (p) => {
        const mi = p.middle_initial ? `${p.middle_initial}. ` : "";
        return `${(p.first_name || "").trim()} ${mi}${(p.last_name || "").trim()}`.trim();
    };
    const initForm = () => {
        const s = splitName(team.coach || "");
        return {
            first_name: s.first_name, middle_initial: s.middle_initial, last_name: s.last_name,
            contact_email: team.contact_email || "",
            contact_phone: team.contact_phone || "",
            identifier: currentId,
            gender: team.gender === "F" ? "F" : "M",
        };
    };
    const [form, setForm] = useState(initForm);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    useEffect(() => {
        setForm(initForm());
    }, [team.id]);

    const genderLabel = form.gender === "F" ? "Girls" : "Boys";

    // Dropdown options come ONLY from the multi-select that was used to create the team.
    const diffOptions = (() => {
        if (diffType === "age_group") return agesForSport(team.sport).map((a) => [a, a]);
        if (diffType === "grade") return GRADES.map((g) => [g, g]);
        if (diffType === "grad_year") return gradYearOptions().map((y) => [String(y), String(y)]);
        return [];
    })();

    const diffTitle = diffType === "grade" ? "Grade" : diffType === "grad_year" ? "Graduation Year" : "Age Group";

    // Recompute the team name from the program base + gender + new identifier.
    const base = baseName || team.name;
    const willHaveSuffix = siblings.length > 1 || team.name !== base;
    const newName = form.identifier && willHaveSuffix ? `${base} ${genderLabel} ${form.identifier}` : base;

    // Duplicate detection — another team in the SAME program ending up with the same name.
    const duplicateWith = siblings.filter((s) => s.id !== team.id && s.name === newName);
    const hasDuplicate = duplicateWith.length > 0;

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                coach: joinName(form),
                contact_email: form.contact_email,
                contact_phone: form.contact_phone,
            };
            const genderChanged = form.gender !== (team.gender === "F" ? "F" : "M");
            const idChanged = diffType && form.identifier !== currentId;
            if (idChanged || genderChanged) {
                if (idChanged) payload[diffType] = form.identifier;
                payload.gender = form.gender;
                payload.name = newName;
            }
            await api.put(`/teams/${team.id}`, payload);
            toast.success(`${newName} saved`);
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const onDelete = async () => {
        const ok = window.confirm(
            `Delete ${team.name}?\n\nThis removes it from the directory, search filters, your program list, and any reviews. This cannot be undone.`
        );
        if (!ok) return;
        setDeleting(true);
        try {
            await api.delete(`/teams/${team.id}`);
            toast.success(`${team.name} deleted`);
            onSaved();  // reloads /my-teams and closes the editor
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally { setDeleting(false); }
    };

    const idPart = currentId;
    const diffLabel = idPart ? `${genderLabel} ${idPart}` : genderLabel;

    const testid = `team-row-${team.id}`;
    return (
        <div className="border border-[#2a2a2a] p-3" data-testid={testid}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                    {hideName ? (
                        <Link to={`/teams/${team.id}`} data-testid={`team-diff-${team.id}`} className="font-display text-lg tracking-wide truncate block hover:text-[#ff4500] transition-colors">
                            {diffLabel}
                        </Link>
                    ) : (
                        <Link to={`/teams/${team.id}`} className="font-display text-lg tracking-wide truncate block hover:text-[#ff4500] transition-colors">
                            {team.name}
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] ml-2">{diffLabel}</span>
                        </Link>
                    )}
                    <div className="text-[11px] text-[#a0a0a0] mt-0.5">
                        Head Coach: <span className="text-white">{team.coach || "—"}</span>
                        {team.contact_email && <> · {team.contact_email}</>}
                        {team.contact_phone && <> · {team.contact_phone}</>}
                    </div>
                </div>
                {!editing && (
                    <button
                        type="button"
                        data-testid={`edit-team-${team.id}`}
                        onClick={onEdit}
                        className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline inline-flex items-center gap-1"
                    >
                        <Edit3 className="h-3 w-3" /> Edit Team
                    </button>
                )}
            </div>
            {editing && (
                <form onSubmit={onSubmit} className="mt-3 space-y-2" data-testid={`edit-team-form-${team.id}`}>
                    {(() => {
                        const inputCls = "mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a] placeholder:text-[#4a4a4a]";
                        const sec = `section-teamrow-${team.id}`;
                        return (
                            <div className="flex flex-wrap items-end gap-2">
                                <div className="w-[92px]">
                                    <SmallSelect label="Gender" testid={`team-gender-${team.id}`} value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} options={[["M", "Boys"], ["F", "Girls"]]} />
                                </div>
                                {diffType && (
                                    <div className="w-[104px]">
                                        <SmallSelect label={diffTitle} testid={`team-identifier-${team.id}`} value={form.identifier} onChange={(v) => setForm({ ...form, identifier: v })} options={diffOptions} required />
                                    </div>
                                )}
                                <div className="w-[118px]">
                                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">First</Label>
                                    <Input data-testid={`coach-first-${team.id}`} autoComplete={`${sec} given-name`} name="given-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} />
                                </div>
                                <div className="w-[46px]">
                                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">M.I.</Label>
                                    <Input data-testid={`coach-mi-${team.id}`} autoComplete={`${sec} additional-name`} name="additional-name" maxLength={1} value={form.middle_initial} onChange={(e) => setForm({ ...form, middle_initial: e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 1).toUpperCase() })} className={`${inputCls} text-center`} />
                                </div>
                                <div className="w-[124px]">
                                    <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">Last</Label>
                                    <Input data-testid={`coach-last-${team.id}`} autoComplete={`${sec} family-name`} name="family-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
                                </div>
                                <div className="w-[210px]">
                                    <SmallField label="Coach Email" type="email" testid={`coach-email-${team.id}`} value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} />
                                </div>
                                <div className="w-[150px]">
                                    <SmallField label="Coach Phone" type="tel" testid={`coach-phone-${team.id}`} value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
                                </div>
                            </div>
                        );
                    })()}
                    <div className="text-[11px] text-[#a0a0a0]">
                        Team name will become <span className="text-white">{newName}</span>
                    </div>
                    {hasDuplicate && (
                        <div className="text-[11px] text-[#ff3b3b]" data-testid={`duplicate-name-warning-${team.id}`}>
                            Heads up: two of your teams will share the name {newName}. That is allowed (expected while you progress each team) — just finish renaming the others to clear the overlap.
                        </div>
                    )}
                    <div className="flex gap-2 justify-between items-center">
                        <button
                            type="button"
                            onClick={onDelete}
                            disabled={deleting || saving}
                            data-testid={`delete-team-${team.id}`}
                            className="h-8 px-3 text-[10px] uppercase tracking-[0.2em] text-[#ff3b3b] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                        >
                            <Trash2 className="h-3 w-3" /> {deleting ? "Deleting…" : "Delete team"}
                        </button>
                        <div className="flex gap-2">
                            <button type="button" onClick={onCancel} className="h-8 px-3 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white">Cancel</button>
                            <Button type="submit" disabled={saving || deleting} data-testid={`save-team-${team.id}`} className="h-8 px-4 text-[10px] rounded-none uppercase tracking-[0.2em] bg-[#ff4500] hover:bg-[#ff5722] font-semibold">
                                {saving ? "Saving…" : "Save"}
                            </Button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}

function SmallField({ label, testid, type = "text", value, onChange, required, maxLength }) {
    return (
        <div>
            <Label className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</Label>
            {type === "tel" ? (
                <PhoneInput
                    data-testid={testid}
                    value={value}
                    onChange={onChange}
                    required={required}
                    className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a] placeholder:text-[#4a4a4a]"
                />
            ) : (
                <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} maxLength={maxLength} className="mt-1 h-9 text-sm rounded-none bg-[#0f0f0f] border-[#2a2a2a] placeholder:text-[#4a4a4a]" />
            )}
        </div>
    );
}

// -------------------- Message Board panel --------------------
function MessageBoardPanel() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);

    const reload = () => {
        setLoading(true);
        api.get("/me/posts")
            .then((r) => setPosts(r.data))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
        setTick((t) => t + 1);
    };
    useEffect(reload, []);

    const submit = async (payload) => {
        await api.post("/me/posts", payload);
        toast.success(payload.scheduled_for ? "Post scheduled" : "Posted to your board");
        reload();
    };

    const remove = async (id) => {
        try {
            await api.delete(`/me/posts/${id}`);
            toast.success("Post removed");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="mt-8" data-testid="message-board-panel">
            <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-4 w-4 text-[#ff4500]" />
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Message board</div>
                <div className="text-[10px] text-[#a0a0a0]">— Followers get an in-app notification (and email once enabled) for every post, based on their own alert settings</div>
            </div>
            <PostComposer onSubmit={submit} accent="#ff4500" placeholder="Post an update to your board — tryouts, results, schedule changes…" testidPrefix="wall-composer" />
            <div className="mt-4"><ScheduledManager key={tick} onChange={reload} /></div>
            {loading ? (
                <div className="text-sm text-[#a0a0a0] mt-3">Loading posts…</div>
            ) : posts.length === 0 ? (
                <div className="text-sm text-[#a0a0a0] mt-3" data-testid="board-empty">No posts yet — your first update to followers will land here.</div>
            ) : (
                <div className="mt-3 space-y-2" data-testid="board-posts">
                    {posts.map((p) => (
                        <div key={p.id} data-testid={`board-post-${p.id}`} className="border border-[#2a2a2a] bg-[#0f0f0f] p-3">
                            <div className="flex gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-2">
                                        {new Date(p.created_at).toLocaleString()}
                                        {p.status === "scheduled" && (
                                            <span data-testid={`board-post-scheduled-${p.id}`} className="text-[9px] font-bold text-[#22c55e] border border-[#22c55e]/40 rounded-full px-1.5 py-0.5 normal-case tracking-normal">
                                                Scheduled · {new Date(p.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>
                                    {p.repost_of ? (
                                        <RepostBody snap={p.repost_of} note={p.body} />
                                    ) : (
                                        <>
                                            {p.body && <div className="text-sm text-white mt-1"><RichText text={p.body} /></div>}
                                            <PostMedia images={p.images} gif={p.gif} />
                                            <PollView postType="wall" postId={p.id} poll={p.poll} accent="#ff4500" />
                                        </>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => remove(p.id)}
                                    data-testid={`board-post-delete-${p.id}`}
                                    className="text-[#666] hover:text-[#ff4500] shrink-0"
                                    title="Delete post"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                            {!p.repost_of && (
                                <PostActions
                                    postType="wall"
                                    postId={p.id}
                                    link={`/u/${user?.username || ""}?tab=posts#post-${p.id}`}
                                    accent="#ff4500"
                                    shareText={p.body}
                                    initial={{ i_reposted: p.i_reposted, repost_count: p.repost_count, i_follow: p.i_follow, follow_count: p.follow_count }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function RepostBody({ snap, note }) {
    const firstLine = (snap.body || "").split("\n")[0].trim();
    const subj = (snap.subject || "").replace(/…$/, "").trim();
    const showSubject = subj && !firstLine.startsWith(subj);
    return (
        <div className="mt-1 text-xs text-[#a0a0a0]" data-testid="repost-body">
            <div className="flex items-center gap-1.5 text-[#22c55e]"><Repeat2 className="h-3.5 w-3.5" /> Reposted</div>
            {note && <div className="text-sm text-white mt-1 whitespace-pre-line break-words" data-testid="repost-note">{note}</div>}
            <a href={snap.link || "#"} className="mt-1 block border-l-2 border-[#2a2a2a] pl-3 hover:border-[#22c55e]">
                <span className="text-[#ff4500] font-semibold">{snap.author_username}</span>
                {showSubject && <span className="text-white block">{snap.subject}</span>}
                <span className="text-[#cfcfcf] block whitespace-pre-line break-words">{snap.body}</span>
                <PostMedia images={snap.images} gif={snap.gif} />
            </a>
        </div>
    );
}

function Row({ icon, label, value, testid }) {
    return (
        <div data-testid={testid} className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center gap-1">
                {icon} {label}
            </div>
            <div className="text-sm text-white mt-0.5 truncate">{value || "—"}</div>
        </div>
    );
}

function ProfileCardPanel() {
    const { user, refresh } = useAuth();
    const [photo, setPhoto] = useState(user?.photo || "");
    const [tipsEnabled, setTipsEnabled] = useState(!!user?.tips_enabled);
    const [tipRows, setTipRows] = useState(() => linksToRows(user?.tip_links));
    const [saving, setSaving] = useState(false);

    const savePhoto = async (url) => {
        setPhoto(url);
        setSaving(true);
        try {
            await api.put("/users/me/profile-card", { photo: url });
            await refresh();
        } catch { toast.error("Could not save photo"); } finally { setSaving(false); }
    };

    const saveTips = async () => {
        setSaving(true);
        try {
            await api.put("/users/me/profile-card", { tips_enabled: tipsEnabled, tip_links: rowsToLinks(tipRows) });
            await refresh();
            toast.success("Tip settings saved");
        } catch { toast.error("Could not save tip settings"); } finally { setSaving(false); }
    };

    return (
        <div className="mt-8" data-testid="profile-card-panel">
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-3">Username avatar</div>
            <div className="border border-[#2a2a2a] bg-[#141414] p-3 space-y-3">
                <div className="max-w-lg">
                    <PhotoUpload value={photo} onChange={savePhoto} circle label="Username avatar (circular)" hint="Circular crop · shown next to your username in the header and beside your name across the site. Separate from your athlete headshot." dragPaste testid="profile-photo-upload" />
                </div>

                <div className="border-t border-[#2a2a2a] pt-4" data-testid="avatar-library-block">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-1">Or pick an avatar</div>
                    <p className="text-[11px] text-[#a0a0a0] mb-3">No photo? Choose a ready-made avatar — it's used everywhere your picture appears.</p>
                    <AvatarPicker value={photo} onSelect={savePhoto} />
                </div>

                <div className="border-t border-[#2a2a2a] pt-4">
                    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="tips-enabled-toggle">
                        <input type="checkbox" checked={tipsEnabled} onChange={(e) => setTipsEnabled(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#16a34a]" />
                        <span className="text-sm text-white leading-tight">Accept tips<span className="block text-[11px] text-[#a0a0a0]">A tip icon appears next to your username. Add your links or username below</span></span>
                    </label>

                    {tipsEnabled && (
                        <div className="mt-3">
                            <TipLinksField value={tipRows} onChange={setTipRows} testidPrefix="tip" />
                        </div>
                    )}

                    <div className="flex justify-end mt-3">
                        <Button type="button" onClick={saveTips} disabled={saving} data-testid="save-tips" className="h-9 px-4 rounded-none text-[10px] uppercase tracking-[0.2em] bg-[#16a34a] hover:bg-[#22c55e] font-semibold">
                            {saving ? "Saving…" : "Save tip settings"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}


function NotificationSettingsPanel() {
    const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })();
    const [channels, setChannels] = useState(DEFAULT_CHANNELS);
    const [defaults, setDefaults] = useState(DEFAULT_DEFAULTS);
    const [weeklyDigest, setWeeklyDigest] = useState(false);
    const [quietHours, setQuietHours] = useState({ enabled: false, start: "22:00", end: "07:00", tz: browserTz });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/users/me/notification-settings")
            .then((r) => {
                setChannels({ ...DEFAULT_CHANNELS, ...(r.data.channels || {}) });
                setDefaults({ ...DEFAULT_DEFAULTS, ...(r.data.defaults || {}) });
                setWeeklyDigest(!!r.data.weekly_digest);
                setQuietHours({ enabled: false, start: "22:00", end: "07:00", tz: browserTz, ...(r.data.quiet_hours || {}) });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [browserTz]);

    const save = async (patch) => {
        setSaving(true);
        try {
            await api.put("/users/me/notification-settings", { channels, defaults, weekly_digest: weeklyDigest, quiet_hours: quietHours, ...patch });
        } catch {
            toast.error("Could not save notification settings");
        } finally {
            setSaving(false);
        }
    };

    const toggleChannel = (key) => {
        const next = { ...channels, [key]: !channels[key] };
        setChannels(next);
        save({ channels: next });
    };

    const toggleDefault = (key) => {
        const next = { ...defaults, [key]: !defaults[key] };
        setDefaults(next);
        save({ defaults: next });
    };

    const toggleDigest = () => {
        const next = !weeklyDigest;
        setWeeklyDigest(next);
        save({ weekly_digest: next });
    };

    const updateQuiet = (patch) => {
        const next = { ...quietHours, tz: browserTz, ...patch };
        setQuietHours(next);
        save({ quiet_hours: next });
    };

    if (loading) return null;

    return (
        <div className="mt-8" data-testid="notification-settings-panel">
            <div className="flex items-center gap-2 mb-3">
                <Bell className="h-4 w-4 text-[#ff4500]" />
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Notification settings</div>
                {saving && <div className="text-[10px] text-[#a0a0a0]">· saving…</div>}
            </div>

            <div className="border border-[#2a2a2a] bg-[#141414] p-4 space-y-5">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">How you are notified</div>
                    <label className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid="channel-in_app">
                        <input type="checkbox" checked={!!channels.in_app} onChange={() => toggleChannel("in_app")} className="mt-0.5 h-4 w-4 accent-[#ff4500]" />
                        <span className="text-sm text-white leading-tight">In-app notifications<span className="block text-[11px] text-[#a0a0a0]">Shown in the bell menu in the header</span></span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid="channel-email">
                        <input type="checkbox" checked={!!channels.email} onChange={() => toggleChannel("email")} className="mt-0.5 h-4 w-4 accent-[#ff4500]" />
                        <span className="text-sm text-white leading-tight">Email<span className="block text-[11px] text-[#a0a0a0]">Email delivery activates once an email provider key is configured</span></span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid="setting-weekly-digest">
                        <input type="checkbox" checked={weeklyDigest} onChange={toggleDigest} className="mt-0.5 h-4 w-4 accent-[#ff4500]" />
                        <span className="text-sm text-white leading-tight">Weekly digest email<span className="block text-[11px] text-[#a0a0a0]">A once-a-week summary of new board posts from everything you follow (sends once email is configured)</span></span>
                    </label>
                </div>

                <div className="border-t border-[#2a2a2a] pt-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Quiet hours</div>
                    <label className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid="quiet-hours-toggle">
                        <input type="checkbox" checked={!!quietHours.enabled} onChange={() => updateQuiet({ enabled: !quietHours.enabled })} className="mt-0.5 h-4 w-4 accent-[#ff4500]" />
                        <span className="text-sm text-white leading-tight">Mute notifications during set hours<span className="block text-[11px] text-[#a0a0a0]">You won't be notified about activity between these times (your time zone: {quietHours.tz})</span></span>
                    </label>
                    {quietHours.enabled && (
                        <div className="flex items-center gap-3 mt-2 pl-7">
                            <label className="flex items-center gap-2 text-sm text-white">
                                From
                                <input type="time" data-testid="quiet-hours-start" value={quietHours.start} onChange={(e) => updateQuiet({ start: e.target.value })} className="bg-[#0f0f0f] border border-[#2a2a2a] px-2 py-1 text-sm text-white outline-none focus:border-[#ff4500]" />
                            </label>
                            <label className="flex items-center gap-2 text-sm text-white">
                                to
                                <input type="time" data-testid="quiet-hours-end" value={quietHours.end} onChange={(e) => updateQuiet({ end: e.target.value })} className="bg-[#0f0f0f] border border-[#2a2a2a] px-2 py-1 text-sm text-white outline-none focus:border-[#ff4500]" />
                            </label>
                        </div>
                    )}
                </div>

                <div className="border-t border-[#2a2a2a] pt-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Default alerts for everyone you follow</div>
                    <div className="text-[11px] text-[#666] mb-2">You can override these per follow on the <Link to="/favorites" className="text-[#ff4500] hover:underline">Following</Link> page.</div>
                    {ALL_EVENT_TYPES.map((t) => (
                        <label key={t} className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid={`default-${t}`}>
                            <input type="checkbox" checked={!!defaults[t]} onChange={() => toggleDefault(t)} className="mt-0.5 h-4 w-4 accent-[#ff4500]" />
                            <span className="text-sm text-white leading-tight">{EVENT_LABELS[t]}<span className="block text-[11px] text-[#a0a0a0]">{EVENT_HELP[t]}</span></span>
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );
}
